import "./TeamPageV2.scss";
import {ArrowDownOutlined, ArrowUpOutlined, CheckCircleFilled, CloseCircleFilled, DownOutlined, EllipsisOutlined, ExclamationCircleFilled} from "@ant-design/icons";
import {Button, Drawer, Dropdown, Menu, Table} from "antd";
import {MemberDto} from "../../../../generated/account-client";
import { mobileState } from "../../../../state/atoms";
import { useRecoilValue } from "recoil";
import { useEffect, useState } from "react";
import { Modal } from "antd";

export enum UsersTableType {
  Active, Pending, Inactive
}

interface TeamPageActiveUsersProps {
  type: UsersTableType;
  users: MemberDto[];
  onMenuOptionClick: (option: string, member: MemberDto) => void;
  onSortClick: (sort: string) => void;
  pagination?: any;
}

export function UsersTable({type, users, onMenuOptionClick, onSortClick, pagination}: TeamPageActiveUsersProps) {
  const mobile = useRecoilValue(mobileState);
  const [mobileSortDrawer, setMobileSortDrawer] = useState(false);
  const [mobileOptionsDrawers, setMobileOptionsDrawers] = useState([] as boolean[]);

  useEffect(() => {
    let array = new Array(users.length).fill(false);
    setMobileOptionsDrawers([...array]);
  }, [users.length]);

  function setOpenMobileOptionsDrawer(index: number) {
    let array = new Array(users.length).fill(false);
    array[index] = true;
    setMobileOptionsDrawers([...array]);
  }

  function setClosedMobileOptionsDrawers() {
    let array = new Array(users.length).fill(false);
    setMobileOptionsDrawers([...array]);
  }

  const menuSortItems = [
    {
      key: "asc",
      label: "Alphabetical (A-Z)",
      icon: <ArrowUpOutlined style={{fontSize: "18px"}}/>,
    },
    {
      key: "desc",
      label: "Alphabetical (Z-A)",
      icon: <ArrowDownOutlined style={{fontSize: "18px"}}/>,
    },
  ]

  const menuOptionItems = (type === UsersTableType.Active || type === UsersTableType.Pending) ? [
    {
      key: "edit-locations",
      label: "Edit Locations",
    },
    {
      key: "edit-permissions",
      label: "Edit Permissions",
    },
  ] : [
    {
      key: "activate-account",
      label: "Activate Account",
    },
    {
      key: "permanently-remove",
      label: "Permanently Remove",
    },
  ];

  if (type === UsersTableType.Active) {
    menuOptionItems.push({
      key: "remove-access",
      label: "Remove Access",
    });
  } else if (type === UsersTableType.Pending) {
    menuOptionItems.push({
      key: "resend-invite",
      label: "Resend Invite",
    });
    menuOptionItems.push({
      key: "cancel-invite",
      label: "Cancel",
    });
  }

  const columns = [
    {
      title: () => (
        mobile ?
        <>
          <span style={{cursor: "pointer"}}>
            Name <DownOutlined onClick={() => setMobileSortDrawer(true)} style={{ fontSize: "14px", transform: "translate(0, 2px)", marginLeft: "4px" }}/>
          </span>
          <Drawer
            open={mobileSortDrawer}
            placement="bottom"
            onClose={() => setMobileSortDrawer(false)}
            title={"Sort by"}
            className="mobile-users-drawer"
            closeIcon={<DownOutlined/>}
            height={230}
          >
            <Menu
              items={menuSortItems}
              onClick={({key}) => {
                onSortClick(key);
                setMobileSortDrawer(false);
              }}
            />
          </Drawer>
        </>
        :
        <Dropdown
          trigger={["click"]}
          menu={{
            items: menuSortItems,
            onClick: ({key}) => onSortClick(key)
          }}
        >
          <span style={{cursor: "pointer"}}>
            Name <DownOutlined style={{ fontSize: "14px", transform: "translate(0, 2px)", marginLeft: "4px" }}/>
          </span>
        </Dropdown>
      ),

      dataIndex: "name",
      key: "name",
      render: (_: any, record: MemberDto) => (
        <div className={"flex"}>
          {type === UsersTableType.Active && <CheckCircleFilled/>}
          {type === UsersTableType.Pending && <ExclamationCircleFilled/>}
          {type === UsersTableType.Inactive && <CloseCircleFilled/>}
          <div className="name-email-mob">
            <span>{record.name}</span>
            {mobile && <span>{record.email}</span>}
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: MemberDto, index: number) => {
        if (record.role === "Owner") return null;
        if (mobile) {
          return <>
            <Button className={"user-set-btn"} onClick={() => setOpenMobileOptionsDrawer(index)}>
              <EllipsisOutlined/>
            </Button>
            <Drawer
              open={mobileOptionsDrawers[index]}
              placement="bottom"
              onClose={() => setClosedMobileOptionsDrawers()}
              title={"More"}
              className="mobile-users-drawer"
              closeIcon={<DownOutlined/>}
              height={270}
            >
              <Menu
                items={menuOptionItems}
                onClick={({key}) => onMenuOptionClick(key, record)}
              />
            </Drawer>
          </>
        } else {
          return <Dropdown
            menu={{
              items: menuOptionItems,
              onClick: ({key}) => {
                if (["remove-access", "cancel-invite", "permanently-remove", "activate-account"].indexOf(key) > -1) {
                  Modal.confirm({
                    title: "Are you sure?",
                    onOk() {
                      onMenuOptionClick(key, record);
                    },
                  });
                } else {
                  onMenuOptionClick(key, record);
                }
              },
            }}
          >
            <Button className={"user-set-btn"}>
              <EllipsisOutlined/>
            </Button>
          </Dropdown>
        }
      },
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={users}
      pagination={pagination || false}
    />
  );
}
